/* eslint-disable @typescript-eslint/no-unsafe-argument */
import axios, { AxiosError, AxiosInstance } from 'axios';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';

import {
  getDefaultRegionalBaseUrl,
  resolveApiBaseUrl,
  setRegionOverride,
} from '@/api/resolve-api-base-url';

let getAccessTokenForApi: (() => Promise<string | null>) | null = null;

export function setApiAccessTokenGetter(getter: (() => Promise<string | null>) | null) {
  getAccessTokenForApi = getter;
  if (getter === null) {
    setRegionOverride(null);
  }
}

const api: AxiosInstance = axios.create({
  baseURL: getDefaultRegionalBaseUrl(),
  withCredentials: false,
  maxRedirects: 0,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: params => {
    const serialize = (v: unknown): string => {
      if (v instanceof Date) return v.toISOString();
      if (v !== null && typeof v === 'object') return JSON.stringify(v);
      return String(v);
    };
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, serialize(v)));
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, serialize(value));
      }
    }
    return searchParams.toString();
  },
});

api.interceptors.request.use(async config => {
  const token = getAccessTokenForApi ? await getAccessTokenForApi() : null;

  config.baseURL = resolveApiBaseUrl({
    accessToken: token,
    method: config.method,
    url: config.url,
  });

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }

  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    if (error?.response?.status === 421 && error.config && !error.config._retried421) {
      const correctRegion: string | undefined = error.response.headers?.['x-correct-region'];
      if (correctRegion === 'us' || correctRegion === 'eu') {
        setRegionOverride(correctRegion);
        error.config._retried421 = true;
        return api(error.config);
      }
    }

    Sentry.withScope(scope => {
      if (error.response) {
        scope.setExtra('response_data', error.response.data);
        scope.setExtra('response_status', error.response.status);

        scope.setTag('error_type', 'api_error');
        scope.setTag('endpoint', error.config.url);
        scope.setTag('http_method', error.config.method);
        scope.setTag('status_code', error.response.status);
      }

      Sentry.captureException(error);
    });

    throw error;
  }
);

export const axiosBaseQuery: BaseQueryFn = async ({ url, method, body, params, headers }) => {
  try {
    const result = await api({
      url,
      method,
      data: body,
      params,
      headers,
    });
    return { data: result.data };
  } catch (axiosError) {
    const err = axiosError as AxiosError;
    return {
      error: {
        status: err.response?.status,
        data: err.response?.data ?? err.message,
      },
    };
  }
};

export { api };
